import React, { useMemo } from 'react';
import { formatTime, formatDate } from '@/utils/timezones';
import type { ClockFormat } from '@/types/clock';

interface DigitalClockProps {
  time: Date;
  format: ClockFormat;
  showSeconds?: boolean;
  showDate?: boolean;
  showTimeZone?: boolean;
  timeZoneName?: string;
  offset?: string;
  textColor?: string;
}

export const DigitalClock: React.FC<DigitalClockProps> = React.memo(({
  time,
  format,
  showSeconds = true,
  showDate = true,
  showTimeZone = true,
  timeZoneName,
  offset,
  textColor = 'text-white'
}) => {
  const timeString = useMemo(() => formatTime(time, format, showSeconds), [time, format, showSeconds]);
  const dateString = useMemo(() => formatDate(time, 'short'), [time]);

  return (
    <div className="flex flex-col items-center justify-center h-full w-full px-8 py-6 pt-12">
      {/* Time Zone Name */}
      {showTimeZone && timeZoneName && (
        <div className={`text-lg font-medium ${textColor} opacity-80 mb-6`}>
          {timeZoneName}
          {offset && <span className="ml-2 text-xs opacity-60">{offset}</span>}
        </div>
      )}

      {/* Main Time Display - NO FLASHING */}
      <div 
        className={`text-8xl font-light tracking-tight ${textColor} tabular-nums transition-opacity duration-300`}
        style={{
          fontFeatureSettings: '"tnum"',
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {timeString}
      </div>

      {/* Date Display */}
      {showDate && (
        <div className={`text-lg font-medium ${textColor} opacity-60 mt-6`}>
          {dateString}
        </div>
      )}
    </div>
  );
});

DigitalClock.displayName = 'DigitalClock';
